import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import toast from "react-hot-toast";
import { useCreateUserMutation } from "../usersApi";
import { useGetBranchesQuery } from "../../admin/adminApi";
import EmployeePicker from "./EmployeePicker";

const CreateUserModal = ({ show, onClose }) => {
  const [mode, setMode] = useState("employee"); // "employee" | "service"
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm();
  const [createUser, { isLoading }] = useCreateUserMutation();
  const { data: branches = [] } = useGetBranchesQuery();
  const employee = watch("employee");

  // Selecting an employee fills Full Name from their record - view-only in
  // this mode, not manually editable. The employee's HR designation (job
  // title, employeeDesignationName) is shown read-only below too, but is
  // NOT what CreateUserRequest.designationId means - that's the separate
  // RBAC default-permission-set designation, unrelated to the employee's
  // actual job title, so it's deliberately left unset here rather than
  // auto-derived from the employee record.
  useEffect(() => {
    if (employee?.employee) {
      const emp = employee.employee;
      const fullName = `${emp.empName || ""} ${emp.empLastName || ""}`
        .replace(/\s+/g, " ")
        .trim();
      if (fullName) setValue("fullName", fullName, { shouldValidate: true });
      setValue("designationId", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  const handleClose = () => {
    reset();
    setMode("employee");
    onClose();
  };

  const onSubmit = async (values) => {
    const body =
      mode === "employee"
        ? {
            username: values.username.trim(),
            password: values.password,
            fullName: values.fullName.trim(),
            employeeRecordGuid: values.employee?.value,
            designationId: values.designationId
              ? Number(values.designationId)
              : null,
            branchIds: null,
          }
        : {
            username: values.username.trim(),
            password: values.password,
            fullName: values.fullName.trim(),
            employeeRecordGuid: null,
            designationId: null,
            branchIds: (values.branchIds || []).map((b) => Number(b.value)),
          };

    if (mode === "employee" && !body.employeeRecordGuid) {
      toast.error("Select an employee to link this user to");
      return;
    }

    try {
      await createUser(body).unwrap();
      toast.success("User created");
      handleClose();
    } catch (error) {
      toast.error(error?.message || "Could not create user");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="btn-group mb-4" role="group">
            <button
              type="button"
              className={`btn ${mode === "employee" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setMode("employee")}
            >
              Linked to Employee
            </button>
            <button
              type="button"
              className={`btn ${mode === "service" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setMode("service")}
            >
              Service Account
            </button>
          </div>

          <div className="row">
            {mode === "employee" && (
              <div className="col-md-12 mb-3">
                <label className="form-label">Employee</label>
                <Controller
                  name="employee"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <EmployeePicker value={field.value} onChange={field.onChange} />
                  )}
                />
                {errors.employee && (
                  <div className="text-danger">Select an employee to link this user to</div>
                )}
                {employee?.employee && (
                  <small className="text-muted">
                    Branch access is auto-derived from the selected employee and cannot be
                    changed here.
                  </small>
                )}
              </div>
            )}

            <div className="col-md-6 mb-3">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-control"
                {...register("username", { required: true })}
              />
              {errors.username && (
                <div className="text-danger">Username is required</div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Temporary Password</label>
              <input
                type="password"
                className="form-control"
                {...register("password", { required: true, minLength: 6 })}
              />
              {errors.password && (
                <div className="text-danger">
                  Password is required (min 6 characters)
                </div>
              )}
            </div>
            <div className="col-md-12 mb-3">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                className={`form-control ${mode === "employee" ? "bg-light" : ""}`}
                readOnly={mode === "employee"}
                {...register("fullName", { required: true })}
              />
              {mode === "employee" && (
                <small className="text-muted">View only - derived from the selected employee.</small>
              )}
              {errors.fullName && (
                <div className="text-danger">Full name is required</div>
              )}
            </div>

            {mode === "employee" ? (
              <div className="col-md-12 mb-3">
                <label className="form-label">Designation</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  readOnly
                  value={
                    employee?.employee?.employeeDesignationName ||
                    (employee ? "No designation on record" : "")
                  }
                />
                <small className="text-muted">
                  View only - the employee&apos;s job title, derived from their Employee
                  Master record. Not editable here.
                </small>
              </div>
            ) : (
              <div className="col-md-12 mb-3">
                <label className="form-label">Branch Access</label>
                <Controller
                  name="branchIds"
                  control={control}
                  render={({ field }) => (
                    <Select
                      isMulti
                      classNamePrefix="react-select"
                      options={branches.map((b) => ({
                        value: b.branchId,
                        label: b.branchCode,
                      }))}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select branches..."
                    />
                  )}
                />
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? "Creating..." : "Create User"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
